import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { TableCell, TableRow, Typography } from '@tempmee/react';
import { PromoCodeStatusLabel, PromoCodeTableCard, promoCodePath } from '../../PromoCodes';

// The ticket's columns. "Discount Name" and "Code Name" lead, matching the
// General Promo Codes list's Name/Code pair (GOTU-7716) — the discount's
// descriptor, then the code an office types to redeem it. "Max Uses" is the
// per-office cap (0 when unlimited); the campaign-wide cap lives on the code's
// own page behind the Action link.
//
// Heads up on "Code Name": it is GOTU-7716's wording, and it is the opposite of
// what "code name" means elsewhere in this feature — PromoCodeBreadcrumbs takes
// `codeName={promoCode.name}` and promoCodes.js searches `name` under that
// label, both meaning the campaign's name. The General list calls this same
// value "Code". Raised with the ticket author; the labels here follow the
// ticket until they say otherwise.
const COLUMNS = [
  'Discount Name',
  'Code Name',
  'Discount',
  'Start Date',
  'End Date',
  'Code Status',
  'Applied',
  '# Used',
  'Max Uses',
  'Action',
];

// A node, not a string, for the reason BestInDentalCycleHistory gives:
// TableCard's default string title is 18px bold, which reads too heavy inside
// the profile tab.
const CARD_TITLE = (
  <Typography variant="subtitle2" as="h2" bold className="truncate text-text-primary">
    Discounts &amp; Promo Codes
  </Typography>
);

/**
 * One office's promo history (GOTU-6663): every code it holds or has redeemed
 * against, newest activity first, with whether and how often it was used. Uses
 * the shared `PromoCodeTableCard` frame — `outline` so the card stands out from
 * the tab's white background.
 *
 * "Applied" is the office-level flag the ticket asks for, and "No" is a row you
 * will actually see: campaign-service #123 (GOTU-5660) added the live campaigns
 * an office matches but has never entered or been swept up by.
 */
export const OfficePromoCodesTable = ({ history, isPending, isError }) => (
  <PromoCodeTableCard
    title={CARD_TITLE}
    outline
    columns={COLUMNS}
    rowCount={history.length}
    isPending={isPending}
    isError={isError}
    emptyMessage="This office has not been on any promotion yet."
    errorMessage="Could not load discounts and promo codes."
  >
    {history.map((row) => (
      <TableRow key={row.campaignId} hover>
        <TableCell>{row.campaignName}</TableCell>
        {/* Codes have no spaces, so a long one would otherwise widen the table
            until the right-hand columns scroll off — the same reason
            PromoCodesTable breaks this cell. QA has a 60-character code on dev. */}
        <TableCell className="break-all">{row.codeLabel}</TableCell>
        <TableCell>{row.discountLabel}</TableCell>
        <TableCell>{row.startDateLabel}</TableCell>
        <TableCell>{row.endDateLabel}</TableCell>
        <TableCell>
          <PromoCodeStatusLabel status={row.status} label={row.statusLabel} />
        </TableCell>
        <TableCell>{row.appliedLabel}</TableCell>
        <TableCell>{row.usedCountLabel}</TableCell>
        <TableCell>{row.maxUsesLabel}</TableCell>
        <TableCell>
          <RouterLink to={promoCodePath(row.campaignId)}>View Code</RouterLink>
        </TableCell>
      </TableRow>
    ))}
  </PromoCodeTableCard>
);

OfficePromoCodesTable.propTypes = {
  history: PropTypes.arrayOf(
    PropTypes.shape({
      campaignId: PropTypes.string.isRequired,
      campaignName: PropTypes.string.isRequired,
      codeLabel: PropTypes.string.isRequired,
      discountLabel: PropTypes.string.isRequired,
      startDateLabel: PropTypes.string.isRequired,
      endDateLabel: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      statusLabel: PropTypes.string.isRequired,
      appliedLabel: PropTypes.string.isRequired,
      usedCountLabel: PropTypes.string.isRequired,
      maxUsesLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
};

export default OfficePromoCodesTable;
